import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import analyticsApi from '~/apis/analytics';
import { StatBar, StorageModeFilter, useStorageModeFilter } from '~/components/common';
import { IPermissionStat } from '~/utils/interface/analytics';

interface IPermissionMeta {
  title: string;
  description: string;
  category: string;
}

const PERMISSION_METADATA: Record<string, IPermissionMeta> = {
  'android.permission.READ_PHONE_STATE': {
    title: 'pages.permissions.metadata.phoneStateCalls.title',
    description: 'pages.permissions.metadata.phoneStateCalls.description',
    category: 'Phone',
  },
  'android.permission.READ_PHONE_NUMBERS': {
    title: 'pages.permissions.metadata.phoneNumberAccess.title',
    description: 'pages.permissions.metadata.phoneNumberAccess.description',
    category: 'Phone',
  },
  'android.permission.CALL_PHONE': {
    title: 'pages.permissions.metadata.directEmergencyCalling.title',
    description: 'pages.permissions.metadata.directEmergencyCalling.description',
    category: 'Phone',
  },
  'android.permission.RECORD_AUDIO': {
    title: 'pages.permissions.metadata.microphoneAudio.title',
    description: 'pages.permissions.metadata.microphoneAudio.description',
    category: 'Audio',
  },
  'android.permission.CAMERA': {
    title: 'pages.permissions.metadata.camera.title',
    description: 'pages.permissions.metadata.camera.description',
    category: 'Media',
  },
  'android.permission.WRITE_EXTERNAL_STORAGE': {
    title: 'pages.permissions.metadata.storageWrite.title',
    description: 'pages.permissions.metadata.storageWrite.description',
    category: 'Storage',
  },
  'android.permission.READ_EXTERNAL_STORAGE': {
    title: 'pages.permissions.metadata.storageRead.title',
    description: 'pages.permissions.metadata.storageRead.description',
    category: 'Storage',
  },
  'android.permission.MANAGE_EXTERNAL_STORAGE': {
    title: 'pages.permissions.metadata.storageManagement.title',
    description: 'pages.permissions.metadata.storageManagement.description',
    category: 'Storage',
  },
  'android.permission.ACCESS_FINE_LOCATION': {
    title: 'pages.permissions.metadata.preciseLocation.title',
    description: 'pages.permissions.metadata.preciseLocation.description',
    category: 'Location',
  },
  'android.permission.ACCESS_COARSE_LOCATION': {
    title: 'pages.permissions.metadata.approximateLocation.title',
    description: 'pages.permissions.metadata.approximateLocation.description',
    category: 'Location',
  },
  'android.permission.ACCESS_BACKGROUND_LOCATION': {
    title: 'pages.permissions.metadata.backgroundLocation.title',
    description: 'pages.permissions.metadata.backgroundLocation.description',
    category: 'Location',
  },
  'android.permission.POST_NOTIFICATIONS': {
    title: 'pages.permissions.metadata.notificationsReminders.title',
    description: 'pages.permissions.metadata.notificationsReminders.description',
    category: 'Notifications',
  },
  'android.permission.SYSTEM_ALERT_WINDOW': {
    title: 'pages.permissions.metadata.displayOverApps.title',
    description: 'pages.permissions.metadata.displayOverApps.description',
    category: 'System',
  },
  DRAW_OVERLAYS: {
    title: 'pages.permissions.metadata.displayOverAppsOverlay.title',
    description: 'pages.permissions.metadata.displayOverAppsOverlay.description',
    category: 'System',
  },
  USE_FULL_SCREEN_INTENT: {
    title: 'pages.permissions.metadata.fullScreenAlerts.title',
    description: 'pages.permissions.metadata.fullScreenAlerts.description',
    category: 'System',
  },
  DISABLE_POWER_SAVE_MODE: {
    title: 'pages.permissions.metadata.ignoreBatteryOptimizations.title',
    description: 'pages.permissions.metadata.ignoreBatteryOptimizations.description',
    category: 'System',
  },
  MIUI_SPECIAL_PERMISSIONS: {
    title: 'pages.permissions.metadata.oemAutostart.title',
    description: 'pages.permissions.metadata.oemAutostart.description',
    category: 'System',
  },
  DEFAULT_DIALER: {
    title: 'pages.permissions.metadata.defaultPhoneApp.title',
    description: 'pages.permissions.metadata.defaultPhoneApp.description',
    category: 'Phone',
  },
  'android.permission.BIND_ACCESSIBILITY_SERVICE': {
    title: 'pages.permissions.metadata.accessibilityProtection.title',
    description: 'pages.permissions.metadata.accessibilityProtection.description',
    category: 'Protection',
  },
  'android.permission.BIND_DEVICE_ADMIN': {
    title: 'pages.permissions.metadata.deviceAdministrator.title',
    description: 'pages.permissions.metadata.deviceAdministrator.description',
    category: 'Protection',
  },
  'android.permission.PACKAGE_USAGE_STATS': {
    title: 'pages.permissions.metadata.appUsageActivity.title',
    description: 'pages.permissions.metadata.appUsageActivity.description',
    category: 'Safety',
  },
  'android.permission.BLUETOOTH': {
    title: 'pages.permissions.metadata.bluetooth.title',
    description: 'pages.permissions.metadata.bluetooth.description',
    category: 'Hardware',
  },
  'android.permission.BLUETOOTH_CONNECT': {
    title: 'pages.permissions.metadata.bluetoothConnection.title',
    description: 'pages.permissions.metadata.bluetoothConnection.description',
    category: 'Hardware',
  },
  'android.permission.ACCESS_NOTIFICATION_POLICY': {
    title: 'pages.permissions.metadata.doNotDisturbOverride.title',
    description: 'pages.permissions.metadata.doNotDisturbOverride.description',
    category: 'System',
  },
};

const formatPermissionInfo = (rawPermission: string): IPermissionMeta => {
  if (PERMISSION_METADATA[rawPermission]) {
    return PERMISSION_METADATA[rawPermission];
  }

  // Graceful fallback for unmapped permissions
  const cleaned = rawPermission
    .replace(/^android\.permission\./i, '')
    .replace(/_/g, ' ')
    .toLowerCase();

  const formattedTitle = cleaned
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return {
    title: formattedTitle || rawPermission,
    description: 'pages.permissions.metadata.fallback.description',
    category: 'System',
  };
};

const getCategoryColor = (category: string) => {
  switch (category.toLowerCase()) {
    case 'phone':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'audio':
    case 'media':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    case 'location':
      return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
    case 'storage':
      return 'bg-amber-500/10 text-amber-600 border-amber-500/20';
    case 'protection':
    case 'safety':
      return 'bg-rose-500/10 text-rose-600 border-rose-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const Permissions = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<IPermissionStat[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [searchQuery, setSearchQuery] = useState('');

  const storageMode = useStorageModeFilter();

  useEffect(() => {
    analyticsApi
      .getPermissions(storageMode)
      .then((res) => setData(res.data.data))
      .catch(() => setError(t('pages.permissions.loadError')))
      .finally(() => setIsLoading(false));
  }, [t, storageMode]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase().trim();
    return data.filter((row) => {
      const info = formatPermissionInfo(row.permission);
      return (
        t(info.title, { defaultValue: info.title }).toLowerCase().includes(query) ||
        t(info.description).toLowerCase().includes(query) ||
        t(`pages.permissions.categories.${info.category.toLowerCase()}`).toLowerCase().includes(query) ||
        row.permission.toLowerCase().includes(query)
      );
    });
  }, [data, searchQuery, t]);

  return (
    <div>
      <div className='flex flex-col justify-between gap-3 sm:flex-row sm:items-center'>
        <div>
          <h1 className='text-xl font-semibold text-foreground'>{t('pages.permissions.title')}</h1>
          <p className='mt-1 text-sm text-muted-foreground'>{t('pages.permissions.description')}</p>
          <StorageModeFilter />
        </div>
        {data && data.length > 0 && (
          <div className='w-full sm:w-64'>
            <input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('pages.permissions.searchPlaceholder')}
              className='w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none'
            />
          </div>
        )}
      </div>

      {isLoading && <p className='mt-6 text-sm text-muted-foreground'>{t('common.loading')}</p>}
      {error && <p className='mt-6 text-sm text-destructive'>{error}</p>}

      {data && data.length === 0 && (
        <p className='mt-6 text-sm text-muted-foreground'>{t('pages.permissions.noEvents')}</p>
      )}

      {data && data.length > 0 && filteredData.length === 0 && (
        <p className='mt-6 text-sm text-muted-foreground'>{t('pages.permissions.noMatches')}</p>
      )}

      {filteredData.length > 0 && (
        <div className='mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2'>
          {filteredData.map((row) => {
            const total = row.granted + row.denied || 1;
            const info = formatPermissionInfo(row.permission);
            const grantedPercent = Math.round((row.granted / total) * 100);

            return (
              <div
                key={row.permission}
                className='flex flex-col justify-between rounded-lg border border-border bg-card p-5 shadow-sm transition hover:border-primary/20'
              >
                <div>
                  <div className='flex flex-wrap items-center justify-between gap-2'>
                    <div className='flex items-center gap-2'>
                      <h3 className='text-base font-semibold text-foreground'>
                        {t(info.title, { defaultValue: info.title })}
                      </h3>
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${getCategoryColor(
                          info.category
                        )}`}
                      >
                        {t(`pages.permissions.categories.${info.category.toLowerCase()}`)}
                      </span>
                    </div>
                    <span className='rounded bg-muted/60 px-2 py-0.5 font-mono text-[11px] text-muted-foreground select-all'>
                      {row.permission}
                    </span>
                  </div>

                  <p className='mt-1.5 text-xs text-muted-foreground leading-relaxed'>
                    {t(info.description)}
                  </p>
                </div>

                <div className='mt-4 flex flex-col gap-2 pt-3 border-t border-border/60'>
                  <div className='flex items-center justify-between text-xs text-muted-foreground'>
                    <span>{t('pages.permissions.grantRate')}</span>
                    <span className='font-semibold text-foreground'>{grantedPercent}%</span>
                  </div>
                  <StatBar label={t('pages.permissions.granted')} value={row.granted} total={total} />
                  <StatBar label={t('pages.permissions.denied')} value={row.denied} total={total} tone='destructive' />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Permissions;
